/**
 * SystemUserSelector - Seletor de usuários do sistema
 * 
 * Componente para selecionar membros do tenant (agentes/usuários internos).
 * Usado para vincular vendedores a usuários do sistema.
 * 
 * @version 1.0.0
 */

import React from 'react';
import { Check, ChevronsUpDown, User, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/useAuth';
import { useTenantMembers } from '@/hooks/useTenantMembers';
import { createLogger } from '@/lib/logger';

// Logger para este módulo
const logger = createLogger('SystemUserSelector');

// Tipo do usuário retornado
interface SystemUser {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

// Props do componente
interface SystemUserSelectorProps {
  value: string | null | undefined;
  onSelect: (userId: string | null, user?: SystemUser | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  allowClear?: boolean;
  /** IDs de usuários já vinculados a outros vendedores (para filtrar) */
  excludeUserIds?: string[];
  /**
   * Usuário pré-carregado para exibir quando `value` está setado mas o
   * usuário não está presente na lista do `useTenantMembers` (ex: ainda
   * carregando, ou membro fora do filtro `onlyActive`). Garante que o
   * trigger sempre mostre o vínculo existente ao abrir o modal de edição.
   */
  fallbackUser?: SystemUser | null;
}

/**
 * Componente para seleção de usuário do sistema
 */
export function SystemUserSelector({
  value,
  onSelect,
  placeholder = 'Vincular a usuário do sistema...',
  disabled = false,
  className,
  allowClear = true,
  excludeUserIds = [],
  fallbackUser = null,
}: SystemUserSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // v1.1.0: Migrado para hook unificado `useTenantMembers` (RPC server-side).
  // Cache compartilhado com CrmUserFilter / UserSelector — evita SELECTs
  // repetidos a tenant_members?tenant_id=eq.<uuid>.
  const { data: rawMembers = [], isLoading } = useTenantMembers({ onlyActive: true });
  const users: SystemUser[] = rawMembers.map((m) => ({
    id: m.user_id,
    full_name: m.full_name,
    email: m.email,
    avatar_url: m.avatar_url,
  }));
  logger.debug('[SystemUserSelector] Usuários carregados:', users.length);

  // Filtra usuários já vinculados a outros vendedores
  const availableUsers = users.filter(u => !excludeUserIds.includes(u.id) || u.id === value);

  // Usuário selecionado atualmente — com fallback para garantir exibição
  // imediata mesmo quando o membro vinculado ainda não está no cache do
  // useTenantMembers (modo edição: usa dados pré-carregados do registro pai).
  const selectedUser =
    users.find(u => u.id === value) ??
    (value && fallbackUser && fallbackUser.id === value ? fallbackUser : undefined);

  // Handler para seleção
  const handleSelect = (userId: string) => {
    if (userId === value) {
      // Clicou no mesmo - limpa seleção
      onSelect(null, null);
    } else {
      const user = users.find(u => u.id === userId);
      onSelect(userId, user || null);
    }
    setOpen(false);
  };

  // Handler para limpar seleção
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null, null);
  };

  if (isLoading) {
    return <Skeleton className="h-10 w-full" />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !selectedUser && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <div className="flex items-center gap-2 truncate">
            {selectedUser ? (
              <>
                <SmartAvatar
                  src={selectedUser.avatar_url}
                  name={selectedUser.full_name}
                  email={selectedUser.email}
                  size="xs"
                  use3DFallback
                />
                <span className="truncate">
                  {selectedUser.full_name || selectedUser.email || 'Usuário sem nome'}
                </span>
              </>
            ) : (
              <>
                <User className="h-4 w-4 opacity-50" />
                <span>{placeholder}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            {allowClear && selectedUser && (
              <X
                className="h-4 w-4 opacity-50 hover:opacity-100"
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar usuário..." />
          <CommandList>
            <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
            <CommandGroup>
              {availableUsers.map(user => (
                <CommandItem
                  key={user.id}
                  value={user.full_name || user.email || user.id}
                  onSelect={() => handleSelect(user.id)}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <SmartAvatar
                    src={user.avatar_url}
                    name={user.full_name}
                    email={user.email}
                    size="sm"
                    use3DFallback
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium truncate">
                      {user.full_name || 'Sem nome'}
                    </span>
                    {user.email && (
                      <span className="text-xs text-muted-foreground truncate">
                        {user.email}
                      </span>
                    )}
                  </div>
                  <Check
                    className={cn(
                      "h-4 w-4 shrink-0",
                      value === user.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Exporta tipo para uso externo
export type { SystemUser };
